import { useState, useMemo, useRef, useId } from 'react';

import { IMAGE_SIZE_MAPPER } from 'utils/constants/ux';
import { getSrcSet } from 'utils/helpers';

import styles from './style.module.css';
import type { Prettify } from 'types/utils';

const ERROR_IMG = '/assets-new/notfound.png';

type ResponsiveImageProps = Prettify<
  React.ComponentProps<'img'> & {
    pictureClassName?: string;
    sources?: { src?: string; media?: string }[];
  }
>;

export function ResponsiveImage({ src, alt = '', className, loading = 'lazy', pictureClassName, sources, ...rest }: ResponsiveImageProps) {
  const id = useId();
  const pictureRef = useRef(null);
  const stop = useRef(false);
  return (
    <picture ref={pictureRef} className={pictureClassName}>
      {sources?.map((s, idx) => (
        <source key={`${id}-source-${idx}`} {...s} />
      ))}
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        onError={({ currentTarget }) => {
          if (stop.current) return;
          currentTarget.onerror = null; // prevents looping
          currentTarget.src = ERROR_IMG;
          currentTarget.style.height = '100%';
          currentTarget.style.objectFit = 'scale-down';
          currentTarget.style.background = 'whitesmoke';
          stop.current = true;
        }}
        {...rest}
      />
    </picture>
  );
}

type ImgProps = React.ComponentProps<'img'>;

export function Img({ src, alt, ...rest }: ImgProps) {
  const [error, setError] = useState(false);
  if (error) {
    return <img className={styles.imageNotFound} alt="" src={ERROR_IMG} />;
  }
  return <img onError={() => setError(true)} className={styles.image} src={src} alt={alt} {...rest} />;
}

// FIXME: podemos hacer que las otherProps no esten en un objecto y que sean props normales
type ProgressiveImgProps = React.ComponentProps<'img'> & {
  iscamperone?: boolean;
  pictureClassName?: string;
};

export function ProgressiveImg({ src, alt, iscamperone, pictureClassName, ...rest }: ProgressiveImgProps) {
  const [error, setError] = useState(false);

  if (error) {
    return <img {...rest} className="h-full bg-faint object-scale-down" alt="" src={ERROR_IMG} loading="lazy" />;
  }

  if (iscamperone) {
    return <img {...rest} onError={() => setError(true)} src={src} alt={alt} />;
  }

  return (
    <picture className={pictureClassName}>
      <source
        type="image/avif"
        srcSet={`
          ${src?.replace(/\.jpg|\.png/, '.avif')}
        `}
      />
      <source
        type="image/webp"
        srcSet={`
          ${src?.replace(/\.jpg|\.png/, '.webp')}
        `}
      />
      <img {...rest} onError={() => setError(true)} src={src} alt={alt} />
    </picture>
  );
}

type ProgressiveBlurredImgProps = {
  highQualitySrc: TODO;
  initLoaded?: boolean;
  alt: string;
  width?: React.ComponentProps<'img'>['width'];
  height?: React.ComponentProps<'img'>['height'];
  index: number;
  loading?: React.ComponentProps<'img'>['loading'];
  pictureClassName?: string;
  target?: string;
};

function ProgressiveBlurredImg({
  highQualitySrc,
  initLoaded = false,
  alt,
  width,
  height,
  index,
  loading = 'lazy',
  pictureClassName,
  target,
}: ProgressiveBlurredImgProps) {
  const id = useId();
  const [isLoaded, setIsLoaded] = useState(initLoaded);
  const [error, setError] = useState(false);
  const isObjectSrcSet = typeof highQualitySrc === 'object' && highQualitySrc !== null;
  const isArraySrcSet = Array.isArray(highQualitySrc);
  const highQualitySrcIsSrcset = Array.isArray(highQualitySrc) || isObjectSrcSet;

  const srcSet: TODO = useMemo(
    () => (isObjectSrcSet && !isArraySrcSet ? highQualitySrc : getSrcSet(highQualitySrc)),
    [highQualitySrc, isObjectSrcSet, isArraySrcSet],
  );

  if (error) {
    return (
      <picture className={`${styles.image} image`}>
        <img className={`${styles.image} ${styles.imageNotFound}`} src={ERROR_IMG} alt="" />
      </picture>
    );
  }

  const imagesNotLazy = [0, 1];

  const filteredSrcSet = Object.entries(srcSet).filter(([key]) => !['xs', 'lg'].includes(key) && typeof srcSet[key as keyof typeof srcSet] !== 'object');
  const { modelDescriptionLabel }: TODO = srcSet as { modelDescriptionLabel: string };

  return (
    <>
      {highQualitySrcIsSrcset ?
        <picture className={`${styles.image} ${isLoaded ? '' : styles.imageNotLoaded} image ${pictureClassName ?? ''}`}>
          {/* TO DO: En el futuro, hacer lo mismo que hacemos en el source de webp para image/avif */}

          {filteredSrcSet.reduce((acc, [srcKey, value]) => {
            if (['md', 'full'].includes(srcKey)) {
              const imageEnd =
                typeof value === 'string' ?
                  value.match(/\.(jpg|png)$/) ?
                    value.match(/\.(jpg|png)$/)![0]
                  : null
                : null;
              if (imageEnd) {
                acc.push(
                  <source
                    key={`${id}-${srcKey}-avif`}
                    type="image/avif"
                    srcSet={srcSet[srcKey]?.replace(imageEnd, '.avif')}
                    media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                  />,
                );
                acc.push(
                  <source
                    key={`${id}-${srcKey}-webp`}
                    type="image/webp"
                    srcSet={srcSet[srcKey]?.replace(imageEnd, '.webp')}
                    media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                  />,
                );
                if (imageEnd === '.png') {
                  acc.push(
                    <source
                      key={`${id}-${srcKey}-png`}
                      data-imgend={imageEnd}
                      type="image/png"
                      srcSet={srcSet[srcKey as keyof typeof srcSet]}
                      media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                    />,
                  );
                } else if (imageEnd === '.jpg') {
                  acc.push(
                    <source
                      key={`${id}-${srcKey}-jpg`}
                      data-imgend={imageEnd}
                      type="image/jpg"
                      srcSet={srcSet[srcKey as keyof typeof srcSet]}
                      media={IMAGE_SIZE_MAPPER[srcKey as keyof typeof IMAGE_SIZE_MAPPER]}
                    />,
                  );
                }
              }
            }

            return acc;
          }, [] as JSX.Element[])}

          <img
            onLoad={() => setIsLoaded(true)}
            onError={() => setError(true)}
            src={srcSet.md}
            width={width}
            height={height}
            alt={alt}
            className={isLoaded ? styles.image : `${styles.image} ${styles.imageNotLoaded}`}
            loading={!loading || imagesNotLazy.includes(index) ? undefined : loading}
          />
        </picture>
      : <img
          onLoad={() => setIsLoaded(true)}
          onError={() => setError(true)}
          className={isLoaded ? styles.image : `${styles.image} ${styles.imageNotLoaded}`}
          src={typeof highQualitySrc === 'string' ? highQualitySrc : (highQualitySrc.md as TODO)}
          alt={alt}
          loading={!loading || imagesNotLazy.includes(index) ? undefined : loading}
        />
      }
      {modelDescriptionLabel && modelDescriptionLabel[target as keyof typeof modelDescriptionLabel] && (
        <div className="absolute bottom-[18px] z-10 flex w-full justify-center text-copy camper:hidden lab:hidden">
          <p className="mb-0 rounded-full bg-[#f6f6f6] px-4 text-[0.8em] shadow-[5px_5px_5px_#d7d7d7]">
            <span className="font-medium">{modelDescriptionLabel[target ?? '']}</span>
          </p>
        </div>
      )}
    </>
  );
}

export default ProgressiveBlurredImg;
